<template>
    <div class="viewport">
        <md-list class="md-double-line" style="padding-top: 0">
            <md-divider></md-divider>
            <md-toolbar :md-elevation="1">
                <span class="md-title">Offerte info</span>
            </md-toolbar>
            <md-list-item>
                <div class="md-layout-item">
                    <md-field>
                        <label>Verkoper</label>
                        <md-select
                            type="text"
                            @md-selected="selectSellerUser"
                            name="quote_sales_user"
                            id="quote_sales_user"
                            v-model="selectedSellerIndex"
                        >
                            <md-option :key="i" v-for="(user, i) of quoteUsers" :value="i">{{
                                `${user.first_name} ${user.last_name}`
                            }}</md-option>
                        </md-select>
                    </md-field>
                </div>
            </md-list-item>
            <md-list-item>
                <div class="md-layout-item">
                    <md-field>
                        <label>Garantie</label>
                        <md-select
                            type="text"
                            name="quote_warranties"
                            id="quote_warranties"
                            v-model="quoteUpsert.data.quote_warranty"
                        >
                            <md-option
                                v-for="(warranty, index) in quoteWarranties"
                                :key="index"
                                :value="warranty.value"
                                >{{ warranty.text }}</md-option
                            >
                        </md-select>
                    </md-field>
                </div>
            </md-list-item>
            <md-list-item>
                <div class="md-layout-item">
                    <md-datepicker v-model="quoteUpsert.data.preferred_placement_date">
                        <label>Select date</label>
                    </md-datepicker>
                </div>
            </md-list-item>
            <md-list-item class="md-double-line">
                <div class="md-layout-item">
                    <md-field style="max-width: 90px">
                        <label>Taal</label>
                        <md-select name="quote-v2-language" id="quote-v2-language" v-model="quoteUpsert.data.language">
                            <md-option :key="Language.nl" :value="Language.nl">NL</md-option>
                            <md-option :key="Language.fr" :value="Language.fr">FR</md-option>
                        </md-select>
                    </md-field>
                </div>
                <div class="md-layout-item md-list-action">
                    <md-field>
                        <label>Bijlages</label>
                        <md-select v-model="selectedDocumentIds" name="attachments" id="quote-v2-attachments" multiple>
                            <md-option
                                v-for="attachment in attachmentOverview.selectable"
                                :key="attachment.document_id"
                                :value="attachment.document_id"
                                >{{ attachment.label }}</md-option
                            >
                        </md-select>
                    </md-field>
                    <div class="md-caption" v-if="attachmentOverview.alwaysIncluded.length > 0">
                        Altijd meegestuurd: {{ alwaysIncludedLabels }}
                    </div>
                    <div
                        class="md-caption"
                        :style="{ color: attachmentOverview.budget.within_budget ? undefined : '#d32f2f' }"
                        v-if="attachmentOverview.budget"
                    >
                        Bijlages {{ formatBytes(attachmentOverview.budget.total_bytes) }} van
                        {{ formatBytes(attachmentOverview.budget.budget_bytes) }}
                        <span v-if="!attachmentOverview.budget.within_budget"
                            >(te veel, de offerte kan niet opgeslagen worden)</span
                        >
                    </div>
                </div>
            </md-list-item>
            <md-list-item>
                <md-icon>mail</md-icon>
                <span class="md-list-item-text">Email offerte naar klant</span>
                <md-switch
                    v-model="sendEmailToCustomer"
                    @change="quoteUpsert.sendEmailToCustomer = sendEmailToCustomer"
                />
            </md-list-item>

            <md-list-item>
                <md-icon>sell</md-icon>
                <span class="md-list-item-text">Verkocht</span>
                <md-switch v-model="sold" @change="quoteUpsert.sold = sold" />
            </md-list-item>
            <md-list-item v-if="sold">
                <signature-view :signature="quoteUpsert.data.signature" @signature-updated="updateSignature" />
            </md-list-item>
        </md-list>
    </div>
</template>

<script lang="ts">
import { Component, Prop, Vue, Watch } from 'vue-property-decorator';
import { IUpsertQuoteData, IUserEntity, Language, QuoteWarranty } from '@phrospr/api-backend';
import { quoteStore } from '@/store/quote/quote.store';
import { QuoteWarrantyCode } from '@phrospr/lib-models';
import { formatBytes } from '@phrospr/lib-core';
import { IQuoteAttachmentOverview, quoteAttachmentOverview } from '@/services/engine/quote-attachment.engine';
import SignatureView from '@/views/signature/signatureView.vue';

@Component({
    components: { SignatureView },
})
export default class QuoteInfo extends Vue {
    @Prop() private quoteUpsert: IUpsertQuoteData;

    // Local copy like `sold` and `sendEmailToCustomer` below: the store's upsertQuote is not
    // reactive (it is not part of the initial state), so a computed on it would never refresh.
    selectedDocumentIds: number[] = [];

    @Watch('selectedDocumentIds')
    onSelectedDocumentIdsChanged(ids: number[]) {
        this.quoteUpsert.document_ids = ids;
    }

    get attachmentOverview(): IQuoteAttachmentOverview {
        return quoteAttachmentOverview(
            this.selectedDocumentIds,
            this.$store.state.quote.attachments,
            this.$store.state.quote.attachmentBudgetBytes,
        );
    }

    get alwaysIncludedLabels(): string {
        return this.attachmentOverview.alwaysIncluded.map(document => document.label).join(', ');
    }

    formatBytes = formatBytes;

    quoteWarranties = Object.keys(QuoteWarranty).map(warranty => {
        return { value: QuoteWarranty[warranty], text: QuoteWarrantyCode[warranty], warranty };
    });

    get quoteUsers(): IUserEntity[] {
        return quoteStore.state.quoteUsers;
    }

    Language = Language;
    sold = false;
    sendEmailToCustomer = true;
    selectedSellerIndex: number = null;

    constructor() {
        super();
        this.load();
    }

    private async load() {
        await Promise.all([quoteStore.dispatch('loadQuoteAttachments'), quoteStore.dispatch('loadQuoteUsers')]);
        this.sold = this.quoteUpsert.sold;
        this.sendEmailToCustomer = this.quoteUpsert.sendEmailToCustomer;
        this.selectedDocumentIds = this.quoteUpsert.document_ids ?? [];
        if (!this.quoteUpsert.data.quote_warranty) {
            this.quoteUpsert.data.quote_warranty = [QuoteWarranty.W1];
        }
        //add logged in user => to have default sold by selected
        if (this.quoteUpsert.quoteId !== undefined) {
            if (typeof this.quoteUpsert.data.sold_by_user_id === 'number')
                this.selectedSellerIndex = this.quoteUsers.findIndex(
                    x => x.user_id === this.quoteUpsert.data.sold_by_user_id,
                );
        } else {
            this.quoteUpsert.data.language = Language.nl;
        }
    }

    selectSellerUser(userListIndex: number) {
        const user = this.quoteUsers[userListIndex];
        this.quoteUpsert.data.sold_by_user_id = user.user_id;
    }

    updateSignature(dataUrl: string) {
        this.quoteUpsert.data.signature = dataUrl;
    }
}
</script>

<style lang="scss" scoped></style>
